'use client'

import {
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import Image from 'next/image'
import Link from 'next/link'
import {
  Button,
  ButtonLink,
} from '@/components/Button'
import { Container } from '@/components/Container'
import { HeaderProps } from './types'

const navItems = [
  { label: 'Quem somos', href: '#historia' },
  { label: 'Serviços', href: '#servicos' },
  { label: 'Atuação', href: '#como-trabalhamos' },
  { label: 'Contato', href: '#contato' },
]

const focusableSelector =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Header({ className = '', ...props }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isMenuOpen) {
      return
    }

    const previousBodyOverflow = document.body.style.overflow

    document.body.style.overflow = 'hidden'
    closeButtonRef.current?.focus()

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        closeMenu()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousBodyOverflow
    }
  }, [isMenuOpen])

  function closeMenu(restoreFocus = true) {
    setIsMenuOpen(false)

    if (restoreFocus) {
      menuButtonRef.current?.focus()
    }
  }

  function handleMobileMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Tab') {
      return
    }

    const focusableElements = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? [],
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (!firstElement || !lastElement) {
      return
    }

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  return (
    <header className={`w-full ${className}`} {...props}>
      <Container className="flex h-20 items-center justify-between gap-8">
        <Link href="/" aria-label="Diversa Advisory - página inicial">
          <Image
            src="/assets/logos/logo-diversa.svg"
            alt="A palavra Diversa em branco, onde o 'v' estilizado ilustra uma figura humana, a cabeça destacada em amarelo-âmbar, representando as pessoas no centro, e uma pílula âmbar abaixo com o nome 'advisory'"
            width={137}
            height={52}
            className="h-10 w-auto"
          />
        </Link>

        <nav aria-label="Navegação principal" className="hidden lg:block">
          <ul className="flex items-center gap-8">
            {navItems.map((item) => (
              <li key={item.href}>
                <a
                  href={item.href}
                  className="inline-flex items-center gap-2 font-sans text-base font-bold leading-5 tracking-normal text-white-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2"
                >
                  {item.label}
                  <Image
                    src="/assets/icons/caret-right.svg"
                    alt=""
                    width={24}
                    height={24}
                    aria-hidden="true"
                    className="size-4"
                  />
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden lg:block">
          <ButtonLink href="#contato">Fale conosco</ButtonLink>
        </div>

        <button
          ref={menuButtonRef}
          type="button"
          className="flex size-11 cursor-pointer items-center justify-center rounded-full bg-[#F2F2F2] focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2 lg:hidden"
          aria-label="Abrir menu"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
          onClick={() => setIsMenuOpen(true)}
        >
          <Image
            src="/assets/icons/list.svg"
            alt=""
            width={24}
            height={24}
            aria-hidden="true"
            className="size-5"
          />
        </button>
      </Container>

      {isMenuOpen ? (
        <div
          ref={menuRef}
          id="mobile-menu"
          className="fixed inset-0 z-50 bg-white-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          onKeyDown={handleMobileMenuKeyDown}
        >
          <Container className="flex min-h-full flex-col py-6">
            <button
              ref={closeButtonRef}
              type="button"
              className="flex size-11 cursor-pointer items-center justify-center rounded-full bg-[#F2F2F2] focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2"
              aria-label="Fechar menu"
              onClick={() => closeMenu()}
            >
              <Image
                src="/assets/icons/arrow-left.svg"
                alt=""
                width={12}
                height={10}
                aria-hidden="true"
                className="size-5"
              />
            </button>

            <nav aria-label="Navegação mobile" className="mt-6">
              <ul>
                {navItems.map((item) => (
                  <li key={item.href} className="border-b border-white-200">
                    <a
                      href={item.href}
                      className="inline-flex items-center gap-2 py-6 font-sans text-base font-bold leading-5 tracking-normal text-white-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2"
                      onClick={() => closeMenu(false)}
                    >
                      {item.label}
                      <Image
                        src="/assets/icons/caret-right.svg"
                        alt=""
                        width={24}
                        height={24}
                        aria-hidden="true"
                        className="size-4"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="mt-auto grid gap-6">
              <Button variant="outline" onClick={() => closeMenu()}>
                Fechar
              </Button>
              <ButtonLink href="#contato" onClick={() => closeMenu(false)}>
                Fale conosco
              </ButtonLink>
            </div>
          </Container>
        </div>
      ) : null}
    </header>
  )
}
